import React from 'react';
import { Box, Card, CardContent, Link, Typography, alpha } from '@mui/material';
import { motion } from 'framer-motion';

export const DETAILS_BLUE_SHADOW = '#1E3A8A';
export const DETAILS_BLUE_700 = '#1D4ED8';

export const DETAILS_SURFACE_BASE = '#0b1220';
export const DETAILS_SURFACE_MID = '#111827';
export const DETAILS_SURFACE_UP = '#1f2937';

export const DETAILS_TEXT_BODY = '#E2E8F0';
export const DETAILS_TEXT_MUTED = '#94A3B8';

export const DETAILS_DEFAULT_NEON = '#38BDF8';
export const DETAILS_NEON_VARIANT_2 = '#818CF8';
export const DETAILS_NEON_VARIANT_3 = '#34D399';

export const DETAILS_PAGE_STYLE_ID = 'details-page-primitives-css';

/** Fonts + keyframes shared by detail pages (pulse, float, value-in). */
export const DETAILS_PAGE_BASE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes details-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes details-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
  @keyframes value-in { from { opacity:0; transform: translateX(-6px); } to { opacity:1; transform: translateX(0); } }
  @keyframes form-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
`;

export function injectDetailsPageStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(DETAILS_PAGE_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = DETAILS_PAGE_STYLE_ID;
  el.innerHTML = DETAILS_PAGE_BASE_CSS;
  document.head.appendChild(el);
}

/**
 * Dot pattern overlay for glass cards. `accentHex` includes # e.g. '#38BDF8'.
 * `fillOpacity` is the SVG attribute value e.g. '0.07' or '0.05'.
 */
export function detailsDotGridUrl(accentHex = '#38BDF8', fillOpacity = '0.07') {
  const hex = accentHex.replace('#', '');
  return `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='0.55' fill='%23${hex}' fill-opacity='${fillOpacity}'/%3E%3C/svg%3E")`;
}

export function detailsCardShellSx({
  neon = DETAILS_DEFAULT_NEON,
  surfaceMid = DETAILS_SURFACE_MID,
  surfaceUp = DETAILS_SURFACE_UP,
  shadowBlue = DETAILS_BLUE_SHADOW,
} = {}) {
  return {
    borderRadius: '16px',
    background: `linear-gradient(160deg, ${alpha(surfaceUp, 0.75)} 0%, ${alpha(surfaceMid, 0.95)} 100%)`,
    border: `1px solid ${alpha(neon, 0.14)}`,
    boxShadow: [
      `0 0 0 1px ${alpha(shadowBlue, 0.72)}`,
      `0 8px 28px ${alpha('#000', 0.28)}`,
      `inset 0 1px 0 ${alpha(neon, 0.08)}`,
    ].join(', '),
    backdropFilter: 'blur(14px)',
    overflow: 'hidden',
    position: 'relative',
    transition: 'box-shadow 0.28s ease, transform 0.28s ease',
  };
}

export const DetailsMotionCard = motion(Card);

export function DetailsPulseDot({ color = DETAILS_NEON_VARIANT_2 }) {
  return (
    <Box sx={{ position: 'relative', width: 10, height: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: alpha(color, 0.9), animation: 'details-pulse 2.8s ease-in-out infinite' }} />
    </Box>
  );
}

/**
 * Animated section card with optional dot-grid overlay (matches Event / User detail layouts).
 */
export function DetailsSectionCard({
  title,
  icon,
  children,
  delay = 0,
  accentColor = DETAILS_DEFAULT_NEON,
  /** Used for outer shell border glow when different from header accent */
  cardShellNeon,
  showDotGrid = false,
  /** User-details style: softer header gradient / border */
  softHeader = false,
  /** User-details style: subtle shadow on header icon box */
  elevatedHeaderIcon = false,
  dotGridUrl,
  cardContentSx,
  /** Optional override for header title colour (e.g. SaaS TEXT_PRIMARY on User details) */
  titleColor,
}) {
  const shellNeon = cardShellNeon ?? accentColor;
  const grid = dotGridUrl ?? (showDotGrid ? detailsDotGridUrl(accentColor) : null);
  const headerBg = softHeader ? 0.035 : 0.04;
  const headerBorder = softHeader ? 0.09 : 0.1;
  const headerTitleColor = titleColor ?? alpha(DETAILS_TEXT_BODY, 0.85);

  return (
    <DetailsMotionCard
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: 'easeOut' }}
      elevation={0}
      sx={detailsCardShellSx({ neon: shellNeon })}
    >
      {grid && (
        <Box sx={{ position: 'absolute', inset: 0, background: grid, pointerEvents: 'none', zIndex: 0, opacity: 0.75 }} />
      )}
      <Box sx={{
        px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.25, sm: 1.5 },
        position: 'relative', zIndex: 1,
        display: 'flex', alignItems: 'center', gap: 1.5,
        background: alpha(accentColor, headerBg),
        borderBottom: `1px solid ${alpha(accentColor, headerBorder)}`,
      }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 26, height: 26, borderRadius: '7px',
          background: alpha(accentColor, 0.12),
          border: `1px solid ${alpha(accentColor, 0.22)}`,
          color: accentColor, flexShrink: 0,
          ...(elevatedHeaderIcon ? { boxShadow: `0 3px 8px ${alpha('#000', 0.22)}` } : {}),
        }}>
          {icon && React.cloneElement(icon, { sx: { fontSize: 13, ...icon.props?.sx } })}
        </Box>
        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.07em', textTransform: 'uppercase', color: headerTitleColor }}>
          {title}
        </Typography>
        <Box sx={{ ml: 'auto' }}>
          <DetailsPulseDot color={accentColor} />
        </Box>
      </Box>
      <CardContent sx={{
        p: { xs: 1.5, sm: 2 }, position: 'relative', zIndex: 1,
        ...cardContentSx,
      }}
      >
        {children}
      </CardContent>
    </DetailsMotionCard>
  );
}

function renderInfoIcon(icon, fontSize) {
  if (!icon) return null;
  if (React.isValidElement(icon)) {
    return React.cloneElement(icon, { sx: { fontSize, ...icon.props?.sx } });
  }
  const Icon = icon;
  return <Icon sx={{ fontSize }} />;
}

/**
 * Label/value tile. Pass `textPrimary` / `textSecondary` for SaaS token overrides (e.g. User details).
 */
export function DetailsInfoField({
  icon,
  label,
  value,
  fullWidth = false,
  mono = false,
  /** Tile border tint (static) */
  neon = DETAILS_DEFAULT_NEON,
  /** Icon + hover accent; defaults to `neon` */
  accent,
  emptyLabel = 'Not specified',
  textPrimary = DETAILS_TEXT_BODY,
  textSecondary = DETAILS_TEXT_MUTED,
  surfaceUp = DETAILS_SURFACE_UP,
  surfaceMid = DETAILS_SURFACE_MID,
  animateValue = false,
  /** When set, value renders as a clickable link (e.g. mailto:, https:) */
  href = null,
  /** Opens in new tab; defaults to true when href looks like http(s) */
  linkExternal,
}) {
  const fieldAccent = accent ?? neon;
  const display = value === 0 ? String(value) : value;
  const showEmpty = display === null || display === undefined || display === '';
  const openExternal = linkExternal ?? (href ? /^https?:\/\//i.test(String(href)) : false);
  const isLinkedTile = Boolean(href);

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        // In column flex parents, avoid flex-grow:1 (old `1 1 240px`) or tiles stretch and create huge gaps between rows.
        flex: fullWidth ? '1 1 100%' : '0 0 auto',
        ...(fullWidth ? { gridColumn: '1 / -1' } : {}),
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        p: 1.2, borderRadius: '9px',
        background: alpha(surfaceUp, 0.58),
        border: `1px solid ${alpha(neon, 0.1)}`,
        transition: 'all 0.2s ease',
        ...(isLinkedTile ? {
          cursor: 'pointer',
          '&:hover': {
            background: alpha(surfaceUp, 0.8),
            border: `1px solid ${alpha(fieldAccent, 0.18)}`,
          },
        } : {}),
      }}
      >
        <Box sx={{
          mt: 0.15,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
          background: alpha(fieldAccent, showEmpty ? 0.05 : 0.1),
          border: `1px solid ${alpha(fieldAccent, showEmpty ? 0.1 : 0.18)}`,
          color: alpha(fieldAccent, 0.85),
        }}
        >
          {renderInfoIcon(icon, 14)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.07em',
            textTransform: 'uppercase', color: alpha(textSecondary, 0.95),
            mb: 0.35, lineHeight: 1.15,
          }}
          >
            {label}
          </Typography>
          {showEmpty ? (
            <Typography sx={{
              fontFamily: mono ? "'JetBrains Mono', monospace" : "'DM Sans', sans-serif",
              fontSize: mono ? '0.8rem' : '0.88rem',
              fontWeight: 500,
              color: alpha(textSecondary, 0.8),
              fontStyle: 'italic',
              letterSpacing: mono ? 0.5 : 0,
              wordBreak: 'break-word',
            }}
            >
              {emptyLabel}
            </Typography>
          ) : href ? (
            <Link
              href={href}
              target={openExternal ? '_blank' : undefined}
              rel={openExternal ? 'noopener noreferrer' : undefined}
              underline="hover"
              sx={{
                fontFamily: mono ? "'JetBrains Mono', monospace" : "'DM Sans', sans-serif",
                fontSize: mono ? '0.8rem' : '0.88rem',
                fontWeight: 500,
                color: alpha(neon, 0.92),
                letterSpacing: mono ? 0.5 : 0,
                wordBreak: 'break-word',
                animation: animateValue ? 'value-in 0.3s ease' : 'none',
              }}
            >
              {display}
            </Link>
          ) : (
            <Typography sx={{
              fontFamily: mono ? "'JetBrains Mono', monospace" : "'DM Sans', sans-serif",
              fontSize: mono ? '0.8rem' : '0.88rem',
              fontWeight: 500,
              color: alpha(textPrimary, 0.94),
              letterSpacing: mono ? 0.5 : 0,
              animation: animateValue ? 'value-in 0.3s ease' : 'none',
              wordBreak: 'break-word',
            }}
            >
              {display}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function DetailsTimelineItem({
  icon,
  label,
  value,
  subValue,
  rowBorderNeon = DETAILS_DEFAULT_NEON,
  iconAccent = DETAILS_NEON_VARIANT_2,
  textPrimary = DETAILS_TEXT_BODY,
  textSecondary = DETAILS_TEXT_MUTED,
  surfaceTile = DETAILS_SURFACE_UP,
}) {
  const showEmpty = value === null || value === undefined || value === '';
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 0.5,
      p: 1.75, borderRadius: '12px',
      background: alpha(surfaceTile, 0.55),
      border: `1px solid ${alpha(rowBorderNeon, 0.07)}`,
      transition: 'all 0.2s ease',
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '6px', flexShrink: 0, background: alpha(iconAccent, 0.14), border: `1px solid ${alpha(iconAccent, 0.22)}`, color: alpha(iconAccent, 0.85) }}>
          {icon && React.cloneElement(icon, { sx: { fontSize: 11, ...icon.props?.sx } })}
        </Box>
        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem', fontWeight: 600, letterSpacing: 1.3, textTransform: 'uppercase', color: alpha(textSecondary, 0.9) }}>
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', fontWeight: 600, color: !showEmpty ? alpha(textPrimary, 0.95) : alpha(textSecondary, 0.8), pl: 0.25 }}>
        {showEmpty ? '—' : value}
      </Typography>
      {subValue && <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.72rem', color: alpha(textSecondary, 0.86), pl: 0.25 }}>{subValue}</Typography>}
    </Box>
  );
}
